import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Separator } from '@/components/ui/separator';
import { Book, Award, Meh, ThumbsDown, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const HelpPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="container mx-auto px-4 py-12 flex-grow">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-col items-center mb-6">
            <div className="self-start mb-4">
              <Link to="/">
                <Button variant="ghost" size="sm" className="flex items-center gap-2 text-bookNavy hover:text-white">
                  <ArrowLeft className="h-4 w-4" />
                  Rate my Cover
                </Button>
              </Link>
            </div>
            <h1 className="text-3xl font-bold text-bookNavy text-center">Help & FAQ</h1>
          </div>
          
          <Separator className="my-6" />
          
          <div className="space-y-8">
            <section>
              <h2 className="text-xl font-semibold text-bookNavy mb-3">How to Interpret Your Ratings</h2>
              
              <div className="space-y-6">
                <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="h-5 w-5 text-green-600" />
                    <h3 className="font-bold text-green-800">"I got a high score (7-9)" - Exquisite</h3>
                  </div>
                  <p className="text-gray-700">
                    That's great! The difference between 7~9 are subtle, so it's difficult to pick up the smaller features for comparison. But as long as you have a seven and above, you're probably fine. Unless it guessed your genre wrong... then you're in trouble.
                  </p>
                </div>
                
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Meh className="h-5 w-5 text-yellow-600" />
                    <h3 className="font-bold text-yellow-800">"I'm in the middle (4-6)" - Mediocre</h3>
                  </div>
                  <p className="text-gray-700">
                    This is murky territory. Your cover probably isn't great, but isn't terrible either. A new cover will help sell more books IF you already have a great blurb and several good reviews. If you don't, I'd focus more on making sure your blurb is very clear and you have enough initial reviews - if your book still isn't selling after that, a new cover will probably make a big difference. But basically at this level you're "good enough for now".
                  </p>
                </div>
                
                <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                  <div className="flex items-center gap-2 mb-2">
                    <ThumbsDown className="h-5 w-5 text-red-600" />
                    <h3 className="font-bold text-red-800">"I got a low score (1-3)" - Aesthetically Challenged</h3>
                  </div>
                  <p className="text-gray-700">
                    If you got a score of 1~3, your cover probably needs a lot of work. It won't be a simple fix of moving a few things around or changing fonts, you might need a whole makeover or a fresh start. I'd like to add a 1-click "redesign this cover option" here, but in the meantime you can use our free book cover creator at 
                    <a href="https://www.DIYBookCovers.com" className="text-blue-600 hover:underline ml-1" target="_blank" rel="noopener noreferrer">
                      DIYBookCovers.com
                    </a> to level up fast.
                  </p>
                </div>
              </div>
            </section>
            
            <Separator />
            
            <section>
              <h2 className="text-xl font-semibold text-bookNavy mb-3">Common Questions</h2>
              
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="font-bold text-gray-800 mb-2">"I disagree with the results"</h3>
                  <p className="text-gray-700">
                    That happens, but we're looking at commercial appeal and marketability. The best cover is the one that is the most clear, not always the one that is the best designed.
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="font-bold text-gray-800 mb-2">"It got some details wrong"</h3>
                  <p className="text-gray-700">
                    Yeah it's not perfect. I trained it carefully from my own 20-year experience as a book cover designer, and I find its rating to be pretty accurate, even when it makes specific mistakes in its critique.
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="font-bold text-gray-800 mb-2">"Will AI steal my book cover?"</h3>
                  <p className="text-gray-700">
                    OpenAI's GPT-4 Vision is the technology behind this tool. Your cover is processed through their API like millions of other images, but is not being specifically stored or repurposed.
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="font-bold text-gray-800 mb-2">"Can I share my results or this tool?"</h3>
                  <p className="text-gray-700">
                    Of course! It's very useful when someone asks for feedback on an ugly cover. You can send them here, and then help them process the results without sounding like a jerk.
                    <br /><br />
                    I'd also like to add a gallery of best and worst covers (with permission of course). I think this is a fun and very useful tool for the indie publishing community. It's free, or at least it comes out of my own pocket as long as I can afford to run it, and I hope to keep it that way.
                  </p>
                </div>
              </div>
            </section>
            
            <div className="mt-10 p-6 bg-bookCream rounded-lg border border-bookNavy/20 shadow-md">
              <div className="flex items-center gap-3 mb-4">
                <Book className="h-6 w-6 text-bookRed" />
                <h2 className="text-xl font-semibold text-bookNavy">About This Tool</h2>
              </div>
              <p className="text-bookGray">
                This tool provides honest feedback on your book cover design based on commercial appeal and marketability principles. 
                Our ratings are designed to help you understand how your cover might perform in the marketplace, 
                not just as an artistic piece. Remember, the goal is to create a cover that clearly communicates 
                genre and content to potential readers!
              </p>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default HelpPage;
